import { useDeferredValue, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import {
  Box,
  Button,
  Center,
  CloseButton,
  Flex,
  Grid,
  GridItem,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Stack,
  Text,
  Tooltip,
  useBreakpointValue,
  VisuallyHidden,
  VStack,
} from '@chakra-ui/react';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  History,
  PanelLeftClose,
  PanelLeftOpen,
  RotateCcw,
  Search,
  X,
} from 'lucide-react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { getLanguage, languageDir } from '@duke-hq/shared-constants/courseTranslationLanguages';
import { iconSize, pxToRem } from '@duke-hq/ui/constants/scalable';
import { sizeVars } from '@duke-hq/ui/constants/layout';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { trpcAdmin, type RouterOutputs } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { toast } from '@duke-hq/app-admin-web/components/ToastContainer';
import {
  useContentTranslations,
  type LanguageTranslation,
  type TranslatableResourceKind,
} from '../useContentTranslations';
import { StatusLabel, StatusLedDot, isTranslatedStatus } from '../TranslationStatusChip';
import { LanguageStatusRow, languageListTemplateColumns } from '../LanguageStatusRow';
import {
  countProvenance,
  groupLabel,
  groupSegments,
  isDirty,
  isQuestionRow,
  nextReviewTargetGroupId,
  overrideStateFor,
  rowMatchesQuery,
  worstStateForGroup,
  type EditableSegment,
  type OverrideState,
  type ProvenanceCounts,
  type SegmentGroup,
} from './segments';
import { EditVersionTimeline, type EditVersionEntry } from './EditVersionTimeline';
import {
  deriveLifecycleSurfaces,
  lifecycleToneColour,
  type LifecyclePhase,
} from './lifecycleSurfaces';
import { testIds } from './testIds';
import { TaggedText, TaggedInput } from './TaggedText';

type OverrideRow = RouterOutputs['translationOverrides']['list']['rows'][number];

type StateFilters = Record<'human' | 'machine' | 'englishShown' | 'needsReReview', boolean>;

type FilterKey = keyof StateFilters;

const FILTER_KEY_FOR_STATE: Record<OverrideState, FilterKey> = {
  human: 'human',
  machine: 'machine',
  'english-shown': 'englishShown',
  'needs-re-review': 'needsReReview',
};

const ALL_FILTERS_ON: StateFilters = {
  human: true,
  machine: true,
  englishShown: true,
  needsReReview: true,
};

type RenderItem =
  | { kind: 'header'; group: SegmentGroup }
  | { kind: 'row'; row: EditableSegment; groupId: string; parentQuestion: string | null };

function matchesFilters(row: EditableSegment, filters: StateFilters): boolean {
  return filters[FILTER_KEY_FOR_STATE[overrideStateFor(row)]];
}

function flattenVisibleRows(
  groups: SegmentGroup[],
  filters: StateFilters,
  query: string,
): RenderItem[] {
  const items: RenderItem[] = [];
  for (const group of groups) {
    const rows = group.segments.filter(
      (row) => matchesFilters(row, filters) && rowMatchesQuery(row, query),
    );
    if (rows.length === 0) {
      continue;
    }
    items.push({ kind: 'header', group });
    for (const row of rows) {
      items.push({
        kind: 'row',
        row,
        groupId: group.groupId,
        parentQuestion: group.parentQuestion,
      });
    }
  }
  return items;
}

// -- State colours ----------------------------------------------------------

const STATE_COLOUR: Record<OverrideState, string> = {
  human: 'green.500',
  machine: 'gray.400',
  'english-shown': 'orange.400',
  'needs-re-review': 'red.500',
};

// -- Provenance count row ----------------------------------------------------

function CountDot({ state }: { state: OverrideState }) {
  return (
    <Box
      boxSize={pxToRem(8)}
      borderRadius="full"
      bg={STATE_COLOUR[state]}
      flexShrink={0}
      aria-hidden
    />
  );
}

// -- Language switcher (header) ----------------------------------------------

function LanguageSwitcher({
  language,
  languages,
  onSelectLanguage,
}: {
  language: string;
  languages: LanguageTranslation[];
  onSelectLanguage?: (code: string) => void;
}) {
  const endonym = getLanguage(language)?.endonym ?? language;
  const englishName = getLanguage(language)?.englishName ?? language;
  const currentStatus = languages.find((entry) => entry.code === language)?.status ?? 'available';

  // The open language always stays listed so the switcher never hides itself.
  const editableLanguages = languages.filter(
    (entry) => (entry.isOffered && entry.status !== 'not-translated') || entry.code === language,
  );

  const crumbLabel = (
    <Text fontSize="sm" noOfLines={1}>
      <Text as="span" fontWeight="semibold">
        {endonym}
      </Text>{' '}
      <Text as="span" fontWeight="normal" color="fg.muted">
        ({englishName})
      </Text>
    </Text>
  );

  if (onSelectLanguage == null || editableLanguages.length === 0) {
    return crumbLabel;
  }

  return (
    <Menu placement="bottom-start">
      <MenuButton
        as={Button}
        variant="secondary"
        size="sm"
        minW={0}
        leftIcon={<StatusLedDot status={currentStatus} />}
        rightIcon={<Icon as={ChevronDown} boxSize={iconSize.sm} />}
        data-testid={testIds.languageSwitcher}
      >
        <HStack spacing={0} minW={0}>
          {crumbLabel}
          <VisuallyHidden>
            <StatusLabel status={currentStatus} />
          </VisuallyHidden>
        </HStack>
      </MenuButton>
      {/* Items pass the menu's grid tracks through with subgrid so the badges share one left edge. */}
      <MenuList
        maxH={pxToRem(420)}
        overflowY="auto"
        display="grid"
        sx={{ gridTemplateColumns: languageListTemplateColumns(), columnGap: 3 }}
      >
        {editableLanguages.map((entry) => {
          const isCurrent = entry.code === language;
          return (
            <MenuItem
              key={entry.code}
              onClick={() => onSelectLanguage(entry.code)}
              data-testid={testIds.languageSwitcherItem}
              data-current={isCurrent ? 'true' : undefined}
              bg={isCurrent ? 'bg.muted' : undefined}
              // Matches the trigger Button's size-sm padding so the labels align.
              sx={{
                display: 'grid',
                gridColumn: '1 / -1',
                gridTemplateColumns: 'subgrid',
                px: '3.5',
              }}
            >
              <LanguageStatusRow
                code={entry.code}
                endonym={entry.endonym}
                englishName={entry.englishName}
                // Dot-sized spacer aligning row names with the trigger label.
                leading={<Box boxSize={pxToRem(8)} flexShrink={0} aria-hidden />}
                status={
                  isTranslatedStatus(entry.status) ? null : (
                    <HStack spacing={1.5}>
                      <StatusLedDot status={entry.status} />
                      <Text fontSize="xs" fontWeight="medium" color="fg.muted" whiteSpace="nowrap">
                        <StatusLabel status={entry.status} />
                      </Text>
                    </HStack>
                  )
                }
              />
            </MenuItem>
          );
        })}
      </MenuList>
    </Menu>
  );
}

// -- Edits crumb (header) ----------------------------------------------------

const BASELINE_ONLY_EDIT_VERSIONS: EditVersionEntry[] = [
  { id: 'baseline', kind: 'baseline', isCurrent: true },
];

function EditsCrumb({
  entries,
  publishedVersion,
  language,
  hasEdits,
  isRestoring,
  onRestoreBaseline,
}: {
  entries: EditVersionEntry[];
  publishedVersion: number | null;
  language: string;
  hasEdits: boolean;
  isRestoring: boolean;
  onRestoreBaseline: () => void;
}) {
  const endonym = getLanguage(language)?.endonym ?? language;
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <>
      <Popover placement="bottom-start">
        <PopoverTrigger>
          <Button
            variant="secondary"
            size="sm"
            flexShrink={0}
            leftIcon={<Icon as={History} boxSize={iconSize.sm} />}
            rightIcon={<Icon as={ChevronDown} boxSize={iconSize.sm} />}
            data-testid={testIds.editsCrumb}
          >
            <Trans>Edits</Trans>
          </Button>
        </PopoverTrigger>
        {/* aria-label: the inner heading isn't associated automatically. */}
        <PopoverContent w={pxToRem(320)} aria-label={t`Edit history`}>
          <PopoverBody p={0}>
            <EditVersionTimeline
              entries={entries}
              publishedVersion={publishedVersion}
              languageLabel={endonym}
              onRestoreBaseline={hasEdits ? () => setConfirmOpen(true) : undefined}
              isRestoring={isRestoring}
            />
          </PopoverBody>
        </PopoverContent>
      </Popover>

      <ConfirmationDialog
        isOpen={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        variant="destructive"
        title={t`Restore the auto-translated version?`}
        body={t`This removes every edit you have made to ${endonym} and rebuilds the translation from the original auto-translated text. Learners see the change once it finishes.`}
        confirmLabel={t`Restore auto-translated`}
        isLoading={isRestoring}
        onConfirm={() => {
          onRestoreBaseline();
          setConfirmOpen(false);
        }}
      />
    </>
  );
}

// -- State filter bar --------------------------------------------------------

const FILTER_ORDER: readonly {
  key: FilterKey;
  state: OverrideState;
  label: React.ReactNode;
  // A thunk: a bare `t` at module level would run before the locale is active and throw.
  tooltip?: () => string;
  testId: string;
}[] = [
  { key: 'human', state: 'human', label: <Trans>Edited</Trans>, testId: testIds.filterHuman },
  {
    key: 'machine',
    state: 'machine',
    label: <Trans>Auto-translated</Trans>,
    tooltip: () => t`Automatically translated, no one has edited this yet.`,
    testId: testIds.filterMachine,
  },
  {
    key: 'englishShown',
    state: 'english-shown',
    label: <Trans>Missing translation</Trans>,
    tooltip: () => t`No translation exists for this text yet, so learners see the English.`,
    testId: testIds.filterUntranslated,
  },
  {
    key: 'needsReReview',
    state: 'needs-re-review',
    label: <Trans>Needs review</Trans>,
    testId: testIds.filterReReview,
  },
];

function countForFilter(counts: ProvenanceCounts, key: FilterKey): number {
  switch (key) {
    case 'human':
      return counts.human;
    case 'machine':
      return counts.machine;
    case 'englishShown':
      return counts.englishShown;
    case 'needsReReview':
      return counts.needsReReview;
  }
}

function StateFilterBar({
  activeFilter,
  counts,
  onFocus,
  onShowAll,
}: {
  // null = showing everything; a key = focused on just that state.
  activeFilter: FilterKey | null;
  counts: ProvenanceCounts;
  onFocus: (key: FilterKey) => void;
  onShowAll: () => void;
}) {
  return (
    <HStack spacing={2} flexWrap="wrap" data-testid={testIds.filterBar}>
      <Text fontSize="xs" fontWeight="semibold" color="fg.muted" textTransform="uppercase">
        <Trans>Show</Trans>
      </Text>
      <Button
        size="xs"
        variant={activeFilter === null ? 'secondary' : 'ghost'}
        aria-pressed={activeFilter === null}
        onClick={onShowAll}
        borderWidth="1px"
        borderColor={activeFilter === null ? 'border.default' : 'transparent'}
      >
        <Text as="span" fontSize="xs">
          <Trans>All</Trans>
        </Text>
      </Button>
      {FILTER_ORDER.map((entry) => {
        const isActive = activeFilter === entry.key;
        const count = countForFilter(counts, entry.key);
        const button = (
          <Button
            key={entry.key}
            size="xs"
            variant={isActive ? 'secondary' : 'ghost'}
            aria-pressed={isActive}
            isDisabled={count === 0}
            onClick={() => onFocus(entry.key)}
            data-testid={entry.testId}
            borderWidth="1px"
            borderColor={isActive ? 'border.default' : 'transparent'}
            opacity={isActive || activeFilter === null ? 1 : 0.6}
          >
            <HStack spacing={1.5}>
              <CountDot state={entry.state} />
              <Text as="span" fontSize="xs">
                {entry.label}
              </Text>
              <Text as="span" fontSize="xs" color="fg.muted">
                {count}
              </Text>
            </HStack>
          </Button>
        );
        return entry.tooltip == null ? (
          button
        ) : (
          <Tooltip key={entry.key} label={entry.tooltip()} openDelay={300}>
            {button}
          </Tooltip>
        );
      })}
    </HStack>
  );
}

// -- Jump-to navigator (left rail) -------------------------------------------

function needsReviewCount(group: SegmentGroup): number {
  return group.segments.filter((row) => {
    const state = overrideStateFor(row);
    return state === 'english-shown' || state === 'needs-re-review';
  }).length;
}

function JumpToRail({
  groups,
  resourceKind,
  needsReviewTotal,
  visibleGroupIds,
  activeGroupId,
  onJumpToGroup,
  onJumpToNeedsReview,
  onCollapse,
}: {
  groups: SegmentGroup[];
  resourceKind?: TranslatableResourceKind;
  needsReviewTotal: number;
  // Group ids with at least one row surviving the active filter/search; entries
  // outside this set have nothing to scroll to, so the rail disables them.
  visibleGroupIds: Set<string>;
  activeGroupId: string | null;
  onJumpToGroup: (groupId: string) => void;
  onJumpToNeedsReview: () => void;
  onCollapse: () => void;
}) {
  return (
    <VStack
      align="stretch"
      spacing={0}
      h="full"
      overflowY="auto"
      bg="bg.subtle"
      borderWidth="1px"
      borderColor="border.default"
      borderRadius="lg"
      data-testid={testIds.outlineRail}
    >
      <Stack spacing={2} px={4} py={3} borderBottomWidth="1px" borderColor="border.default">
        <HStack justify="space-between" align="center">
          <Text fontSize="xs" fontWeight="semibold" color="fg.muted" textTransform="uppercase">
            <Trans>Jump to</Trans>
          </Text>
          <IconButton
            size="xs"
            variant="ghost"
            icon={<Icon as={PanelLeftClose} boxSize={iconSize.sm} />}
            aria-label={t`Hide the outline`}
            onClick={onCollapse}
            data-testid={testIds.outlineCollapse}
          />
        </HStack>
        <Text fontSize="xs" color="fg.muted">
          {resourceKind === 'PAGE' ? (
            <Trans>
              {groups.length} sections · {needsReviewTotal} need review
            </Trans>
          ) : (
            <Trans>
              {groups.length} nodes · {needsReviewTotal} need review
            </Trans>
          )}
        </Text>
        {needsReviewTotal > 0 ? (
          <Button
            size="xs"
            variant="secondary"
            alignSelf="start"
            onClick={onJumpToNeedsReview}
            data-testid={testIds.nextNeedsAttention}
          >
            <Trans>Next to review</Trans>
          </Button>
        ) : null}
      </Stack>

      <VStack align="stretch" spacing={0}>
        {groups.map((group) => {
          const needsReview = needsReviewCount(group);
          const isActive = group.groupId === activeGroupId;
          const isVisible = visibleGroupIds.has(group.groupId);
          const label = groupLabel(group) ?? undefined;
          return (
            <HStack
              key={group.groupId}
              as="button"
              type="button"
              disabled={!isVisible}
              onClick={() => onJumpToGroup(group.groupId)}
              spacing={2}
              px={4}
              py={2}
              textAlign="start"
              cursor={isVisible ? 'pointer' : 'not-allowed'}
              opacity={isVisible ? 1 : 0.4}
              borderBottomWidth="1px"
              borderColor="border.default"
              borderInlineStartWidth={pxToRem(3)}
              borderInlineStartColor={isActive ? 'border.active' : 'transparent'}
              bg={isActive ? 'bg.subtle' : undefined}
              _hover={isVisible ? { bg: 'bg.subtle' } : undefined}
              _last={{ borderBottomWidth: 0 }}
              data-testid={testIds.outlineEntry}
              data-active={isActive ? 'true' : undefined}
            >
              <Box
                boxSize={pxToRem(8)}
                borderRadius="full"
                bg={STATE_COLOUR[worstStateForGroup(group)]}
                flexShrink={0}
                aria-hidden
              />
              <Text
                fontSize="sm"
                color="fg.default"
                fontWeight={isActive ? 'medium' : 'normal'}
                noOfLines={1}
                dir="ltr"
                flex="1"
                title={label}
              >
                {label ?? <Trans>Page content</Trans>}
              </Text>
              {needsReview > 0 ? (
                <Tooltip label={t`${needsReview} segments need review`} openDelay={300}>
                  <Box
                    as="span"
                    fontSize="xs"
                    fontWeight="semibold"
                    color="warning.emphasis"
                    flexShrink={0}
                    aria-label={t`${needsReview} segments need review`}
                  >
                    {needsReview}
                  </Box>
                </Tooltip>
              ) : null}
            </HStack>
          );
        })}
      </VStack>
    </VStack>
  );
}

// -- Seam row ----------------------------------------------------------------

// The target cell is plain text until clicked, so few live text inputs exist at once.
function SeamRow({
  row,
  parentQuestion,
  dir,
  isEditing,
  isReadOnly,
  onStartEditing,
  onChange,
  onResetToMachine,
}: {
  row: EditableSegment;
  parentQuestion: string | null;
  dir: 'ltr' | 'rtl';
  isEditing: boolean;
  isReadOnly: boolean;
  onStartEditing: () => void;
  onChange: (value: string) => void;
  onResetToMachine: () => void;
}) {
  const isReply = !isQuestionRow(row);
  // A stale override the author has emptied via Reset is on its way to the
  // machine value, so the whole row de-escalates now (rail, caption) rather than
  // staying red until save persists the delete.
  const resetPending = overrideStateFor(row) === 'needs-re-review' && row.draftValue.trim() === '';
  const state = resetPending ? 'machine' : overrideStateFor(row);
  const isStale = state === 'needs-re-review';

  // currentMachineValue is the clean machine baseline (from ContentTranslation.machineValues),
  // so it holds the pre-override value even for an overridden row.
  const revertsTo = row.segment.currentMachineValue ?? t`Add a translation`;

  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (isEditing) {
      inputRef.current?.focus();
    }
  }, [isEditing]);

  return (
    <Grid
      templateColumns={`1fr ${pxToRem(1)} 1fr`}
      borderBottomWidth="1px"
      borderColor="border.default"
      data-testid={testIds.segmentRow}
    >
      <GridItem px={4} py={3} minW={0}>
        {isReply && parentQuestion != null ? (
          <Text
            fontSize="xs"
            color="fg.muted"
            dir="ltr"
            noOfLines={1}
            data-testid={testIds.breadcrumb}
          >
            <Trans>Reply to: {parentQuestion}</Trans>
          </Text>
        ) : null}
        <TaggedText
          value={row.segment.sourceText}
          dir="ltr"
          fontSize="sm"
          data-testid={testIds.sourceText}
        />
      </GridItem>

      <GridItem bg="border.default" aria-hidden />

      <GridItem position="relative" px={4} py={3} minW={0}>
        {/* Physical left, not insetStart: the rail pins to a fixed edge even for RTL content. */}
        <Box
          position="absolute"
          left={0}
          top={0}
          bottom={0}
          width={pxToRem(3)}
          bg={STATE_COLOUR[state]}
          aria-hidden
          data-testid={testIds.stateRail}
        />
        {isEditing && !isReadOnly ? (
          <TaggedInput
            value={row.draftValue}
            seedValue={row.segment.currentMachineValue ?? ''}
            dir={dir}
            onChange={onChange}
            placeholder={revertsTo}
            inputRef={inputRef}
            data-testid={testIds.targetInput}
          />
        ) : (
          <Box
            as="button"
            type="button"
            onClick={isReadOnly ? undefined : onStartEditing}
            disabled={isReadOnly}
            display="block"
            width="full"
            textAlign={dir === 'rtl' ? 'right' : 'left'}
            cursor={isReadOnly ? 'not-allowed' : 'text'}
            borderRadius="sm"
            _hover={isReadOnly ? undefined : { bg: 'bg.muted' }}
            data-testid={testIds.editAffordance}
          >
            <TaggedText
              value={row.draftValue.trim() === '' ? revertsTo : row.draftValue}
              dir={dir}
              fontSize="sm"
              color={row.draftValue.trim() === '' ? 'fg.muted' : 'fg.default'}
              data-testid={testIds.targetText}
            />
          </Box>
        )}

        {isStale && !isReadOnly ? (
          <HStack spacing={3} mt={2} align="center">
            <Text fontSize="xs" color="fg.muted" dir="ltr" data-testid={testIds.staleCaption}>
              <Trans>English changed since you wrote this</Trans>
            </Text>
            <Tooltip label={t`Reset to the auto-translated text`} openDelay={300}>
              <Button
                size="xs"
                variant="secondary"
                ml="auto"
                flexShrink={0}
                leftIcon={<Icon as={RotateCcw} boxSize={iconSize.xs} />}
                onClick={onResetToMachine}
                data-testid={testIds.resetToMachine}
              >
                <Trans>Reset</Trans>
              </Button>
            </Tooltip>
          </HStack>
        ) : null}
      </GridItem>
    </Grid>
  );
}

// -- Presentational workbench ------------------------------------------------

// Presentational; OverrideWorkbenchData below owns the queries and save plumbing.
export function OverrideWorkbenchView({
  language,
  languages = [],
  resourceKind,
  publishedVersion,
  editVersions = BASELINE_ONLY_EDIT_VERSIONS,
  groups,
  isLoading = false,
  isError = false,
  phase = 'idle',
  dirtyCount,
  appliedEditCount = null,
  attemptId = 0,
  onBack,
  closeButtonRef,
  onRetryLoad,
  onChange,
  onResetToMachine,
  onSave,
  onSelectLanguage,
  onRetryRebuild,
  isRetryingRebuild = false,
  onRestoreBaseline,
  isRestoringBaseline = false,
  fillHeight = false,
}: {
  language: string;
  languages?: LanguageTranslation[];
  resourceKind?: TranslatableResourceKind;
  publishedVersion?: number | null;
  // Newest first.
  editVersions?: EditVersionEntry[];
  groups: SegmentGroup[];
  isLoading?: boolean;
  isError?: boolean;
  phase?: LifecyclePhase;
  dirtyCount: number;
  appliedEditCount?: number | null;
  attemptId?: number;
  onBack?: () => void;
  closeButtonRef?: RefObject<HTMLButtonElement | null>;
  onRetryLoad?: () => void;
  onChange?: (segmentKey: string, value: string) => void;
  onResetToMachine?: (row: EditableSegment) => void;
  onSave?: () => void;
  onSelectLanguage?: (code: string) => void;
  onRetryRebuild?: () => void;
  isRetryingRebuild?: boolean;
  onRestoreBaseline?: () => void;
  isRestoringBaseline?: boolean;
  fillHeight?: boolean;
}) {
  const dir = languageDir(language);
  const englishName = getLanguage(language)?.englishName ?? language;

  // The grid locks while a rebuild applies so a second save can't race the
  // one-job-per-(version, language) invariant.
  const surfaces = deriveLifecycleSurfaces({
    phase,
    dirtyCount,
    appliedEditCount,
    attemptId,
    enqueueing: isRetryingRebuild,
  });
  const isGridLocked = surfaces.gridLocked;

  const counts = useMemo(() => countProvenance(groups), [groups]);

  const [activeFilter, setActiveFilter] = useState<FilterKey | null>(null);
  const filters = useMemo<StateFilters>(
    () =>
      activeFilter === null
        ? ALL_FILTERS_ON
        : {
            human: activeFilter === 'human',
            machine: activeFilter === 'machine',
            englishShown: activeFilter === 'englishShown',
            needsReReview: activeFilter === 'needsReReview',
          },
    [activeFilter],
  );
  const focusFilter = (key: FilterKey) =>
    setActiveFilter((current) => (current === key ? null : key));
  const [searchQuery, setSearchQuery] = useState('');
  // The input updates every keystroke; re-flattening 1,900 rows runs on the
  // deferred value so typing stays responsive under a heavy grid.
  const deferredQuery = useDeferredValue(searchQuery);
  const [editingKey, setEditingKey] = useState<string | null>(null);

  // Auto-collapses the outline at narrow widths; a manual toggle wins once used.
  const narrowViewport = useBreakpointValue({ base: true, lg: false }) ?? false;
  const [railOverride, setRailOverride] = useState<boolean | null>(null);
  const railCollapsed = railOverride ?? narrowViewport;
  const needsReviewTotal = useMemo(
    () => groups.reduce((sum, group) => sum + needsReviewCount(group), 0),
    [groups],
  );

  useEffect(() => {
    if (isGridLocked) {
      setEditingKey(null);
    }
  }, [isGridLocked]);

  const scrollRef = useRef<HTMLDivElement>(null);

  const visibleRows = useMemo(
    () => flattenVisibleRows(groups, filters, deferredQuery),
    [groups, filters, deferredQuery],
  );
  const visibleGroupIds = useMemo(
    () =>
      new Set(
        visibleRows.filter((item) => item.kind === 'header').map((item) => item.group.groupId),
      ),
    [visibleRows],
  );

  const virtualizer = useVirtualizer({
    count: visibleRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 96,
    overscan: 8,
  });

  // While a click-driven scroll settles the clicked destination wins, so the
  // rail doesn't flick through every group it flies past.
  const [activeGroupId, setActiveGroupId] = useState<string | null>(null);
  const programmaticScrollUntil = useRef(0);
  const [pulsedGroupId, setPulsedGroupId] = useState<string | null>(null);
  // A jump whose target isn't in the current view (a filter/search hides it) is
  // parked here; clearing those obstructions re-renders visibleRows, and the
  // effect below fires the scroll once the target row exists.
  const pendingJumpGroupId = useRef<string | null>(null);

  const virtualItems = virtualizer.getVirtualItems();
  useEffect(() => {
    if (Date.now() < programmaticScrollUntil.current) {
      return;
    }
    const topHeader = virtualItems.find((item) => visibleRows[item.index]?.kind === 'header');
    const topItem = topHeader ?? virtualItems[0];
    if (topItem === undefined) {
      return;
    }
    const row = visibleRows[topItem.index];
    const groupId = row?.kind === 'header' ? row.group.groupId : (row?.groupId ?? null);
    setActiveGroupId(groupId);
  }, [virtualItems, visibleRows]);

  const jumpToGroup = (groupId: string) => {
    const index = visibleRows.findIndex(
      (item) => item.kind === 'header' && item.group.groupId === groupId,
    );
    if (index < 0) {
      return;
    }
    programmaticScrollUntil.current = Date.now() + 250;
    setActiveGroupId(groupId);
    virtualizer.scrollToIndex(index, { align: 'start' });
    setPulsedGroupId(groupId);
    window.setTimeout(() => {
      setPulsedGroupId((current) => (current === groupId ? null : current));
    }, 500);
  };

  // Latest jumpToGroup, so the parked-jump effect can call it without taking the
  // recreated-every-render closure as a dependency (which would re-run the effect).
  const jumpToGroupRef = useRef(jumpToGroup);
  jumpToGroupRef.current = jumpToGroup;

  // Fire a parked jump once the target row lands in the view (after clearing the
  // filter/search that hid it). No-op until then, and clears itself once matched.
  useEffect(() => {
    const target = pendingJumpGroupId.current;
    if (target === null) {
      return;
    }
    if (visibleRows.some((item) => item.kind === 'header' && item.group.groupId === target)) {
      pendingJumpGroupId.current = null;
      jumpToGroupRef.current(target);
    }
  }, [visibleRows]);

  const jumpToNeedsReview = () => {
    const targetGroupId = nextReviewTargetGroupId(groups);
    if (targetGroupId === null) {
      return;
    }
    const isVisible = visibleRows.some(
      (item) => item.kind === 'header' && item.group.groupId === targetGroupId,
    );
    if (isVisible) {
      jumpToGroup(targetGroupId);
      return;
    }
    // The active filter or search hides the target; clear both and let the effect
    // above jump once it reappears, rather than the jump being a silent no-op.
    setActiveFilter(null);
    setSearchQuery('');
    pendingJumpGroupId.current = targetGroupId;
  };

  return (
    <Flex
      direction="column"
      h={fillHeight ? '100%' : `calc(100vh - ${sizeVars.navbar})`}
      bg="bg.canvas"
      data-testid={testIds.workbench}
    >
      <Box
        position="sticky"
        top={0}
        zIndex={1}
        px={6}
        py={4}
        bg="bg.surface"
        borderBottomWidth="1px"
        borderColor="border.default"
        data-testid={testIds.header}
      >
        <Flex justify="space-between" align="center" gap={4}>
          <HStack spacing={2} minW={0}>
            {publishedVersion != null ? (
              <>
                <Tooltip
                  label={t`The published version of this resource. Translations are generated from it.`}
                >
                  <Box
                    // Focusable so the tooltip is keyboard-reachable; not a control.
                    tabIndex={0}
                    borderWidth="1px"
                    borderColor="border.default"
                    bg="bg.surface"
                    borderRadius="md"
                    fontSize="xs"
                    fontWeight="semibold"
                    color="fg.muted"
                    px={2}
                    py={1}
                    whiteSpace="nowrap"
                    cursor="default"
                    flexShrink={0}
                  >
                    <Trans>Published v{publishedVersion}</Trans>
                  </Box>
                </Tooltip>
                <Icon
                  as={ChevronRight}
                  boxSize={iconSize.xs}
                  color="fg.muted"
                  flexShrink={0}
                  aria-hidden
                />
              </>
            ) : null}
            <LanguageSwitcher
              language={language}
              languages={languages}
              onSelectLanguage={onSelectLanguage}
            />
            <Icon
              as={ChevronRight}
              boxSize={iconSize.xs}
              color="fg.muted"
              flexShrink={0}
              aria-hidden
            />
            <EditsCrumb
              entries={editVersions}
              publishedVersion={publishedVersion ?? null}
              language={language}
              hasEdits={editVersions.some((entry) => entry.kind === 'edit')}
              isRestoring={isRestoringBaseline}
              onRestoreBaseline={onRestoreBaseline ?? (() => {})}
            />
          </HStack>

          <HStack spacing={5} flexShrink={0}>
            <SaveControl surfaces={surfaces} onSave={onSave} onRetry={onRetryRebuild} />
            {onBack != null ? (
              <CloseButton
                ref={closeButtonRef}
                size="lg"
                onClick={onBack}
                aria-label={t`Close translation editor`}
                data-testid={testIds.backLink}
              />
            ) : null}
          </HStack>
        </Flex>

        <FailedStrip strip={surfaces.strip} />
      </Box>

      {isError ? (
        <VStack
          align="center"
          justify="center"
          flex="1"
          spacing={3}
          data-testid={testIds.emptyState}
        >
          <Text fontSize="sm" color="fg.muted">
            <Trans>Couldn't load the text to translate</Trans>
          </Text>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => onRetryLoad?.()}
            data-testid={testIds.errorRetry}
          >
            <Trans>Retry</Trans>
          </Button>
        </VStack>
      ) : isLoading ? (
        <Center flex="1">
          <Spinner size="lg" />
        </Center>
      ) : groups.length === 0 ? (
        <Center flex="1" data-testid={testIds.emptyState}>
          <Text fontSize="sm" color="fg.muted">
            <Trans>This resource has no translatable text yet.</Trans>
          </Text>
        </Center>
      ) : (
        <Grid
          templateColumns={`${railCollapsed ? pxToRem(48) : pxToRem(272)} 1fr`}
          gap={3}
          p={3}
          flex="1"
          minH={0}
          bg="bg.canvas"
        >
          {railCollapsed ? (
            <VStack
              align="center"
              spacing={2}
              py={2}
              bg="bg.subtle"
              borderWidth="1px"
              borderColor="border.default"
              borderRadius="lg"
              data-testid={testIds.outlineRail}
            >
              <Tooltip label={t`Show the outline`} openDelay={300}>
                <IconButton
                  size="sm"
                  variant="ghost"
                  icon={<Icon as={PanelLeftOpen} boxSize={iconSize.md} />}
                  aria-label={t`Show the outline`}
                  onClick={() => setRailOverride(false)}
                  data-testid={testIds.outlineExpand}
                />
              </Tooltip>
              {needsReviewTotal > 0 ? (
                <Tooltip label={t`${needsReviewTotal} segments need review`} openDelay={300}>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="warning.emphasis"
                    aria-label={t`${needsReviewTotal} segments need review`}
                  >
                    {needsReviewTotal}
                  </Text>
                </Tooltip>
              ) : null}
            </VStack>
          ) : (
            <JumpToRail
              groups={groups}
              resourceKind={resourceKind}
              needsReviewTotal={needsReviewTotal}
              visibleGroupIds={visibleGroupIds}
              activeGroupId={activeGroupId}
              onJumpToGroup={jumpToGroup}
              onJumpToNeedsReview={jumpToNeedsReview}
              onCollapse={() => setRailOverride(true)}
            />
          )}

          {/* The chip sits outside the faded layer so it stays legible while the grid dims. */}
          <Box position="relative" minW={0} minH={0} display="flex">
            {surfaces.chip.visible ? (
              <Center position="absolute" inset={0} zIndex={1} pointerEvents="none">
                <HStack
                  spacing={2}
                  px={3}
                  py={1.5}
                  bg="bg.surface"
                  borderWidth="1px"
                  borderColor="border.default"
                  borderRadius="full"
                  boxShadow="md"
                  data-testid={testIds.readOnlyNote}
                >
                  <Spinner size="xs" color="info.emphasis" flexShrink={0} />
                  <Text fontSize="xs" fontWeight="semibold" whiteSpace="nowrap">
                    {surfaces.chip.label}
                  </Text>
                </HStack>
              </Center>
            ) : null}
            <Flex
              direction="column"
              flex="1"
              minW={0}
              bg="bg.surface"
              borderWidth="1px"
              borderColor="border.default"
              borderRadius="lg"
              overflow="hidden"
              opacity={isGridLocked ? 0.6 : 1}
              transition="opacity 150ms ease"
            >
              <Flex
                px={4}
                py={2}
                gap={3}
                align="center"
                justify="space-between"
                borderBottomWidth="1px"
                borderColor="border.default"
                bg="bg.surface"
              >
                <StateFilterBar
                  activeFilter={activeFilter}
                  counts={counts}
                  onFocus={focusFilter}
                  onShowAll={() => setActiveFilter(null)}
                />
                <InputGroup size="sm" w={pxToRem(240)} flexShrink={0}>
                  <InputLeftElement pointerEvents="none">
                    <Icon as={Search} boxSize={iconSize.sm} color="fg.muted" />
                  </InputLeftElement>
                  <Input
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                    placeholder={t`Search text`}
                    aria-label={t`Search the source and translated text`}
                    borderRadius="md"
                    data-testid={testIds.searchInput}
                  />
                  {searchQuery !== '' ? (
                    <InputRightElement>
                      <Box
                        as="button"
                        type="button"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        boxSize={pxToRem(18)}
                        borderRadius="full"
                        color="fg.muted"
                        _hover={{ bg: 'bg.muted', color: 'fg.default' }}
                        onClick={() => setSearchQuery('')}
                        aria-label={t`Clear the search`}
                        data-testid={testIds.searchClear}
                      >
                        <Icon as={X} boxSize={iconSize.xs} />
                      </Box>
                    </InputRightElement>
                  ) : null}
                </InputGroup>
              </Flex>
              <Grid
                templateColumns="1fr 1fr"
                px={4}
                py={2}
                borderBottomWidth="1px"
                borderColor="border.default"
                bg="bg.surface"
              >
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  color="fg.muted"
                  textTransform="uppercase"
                >
                  <Trans>English source</Trans>
                </Text>
                <HStack spacing={2} minW={0}>
                  <Text
                    fontSize="xs"
                    fontWeight="semibold"
                    color="fg.muted"
                    textTransform="uppercase"
                    dir="ltr"
                    noOfLines={1}
                  >
                    <Trans>{englishName} translation</Trans>
                  </Text>
                </HStack>
              </Grid>
              <Box ref={scrollRef} overflowY="auto" flex="1" data-testid={testIds.seamGrid}>
                {visibleRows.length === 0 ? (
                  <VStack align="center" justify="center" h="full" spacing={3} py={8}>
                    <Text fontSize="sm" color="fg.muted">
                      {/* Reads the deferred value so the message and the rows it describes change on the same frame. */}
                      {deferredQuery.trim() !== '' ? (
                        <Trans>No rows match your search.</Trans>
                      ) : (
                        <Trans>No rows match the current filters.</Trans>
                      )}
                    </Text>
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => {
                        setActiveFilter(null);
                        setSearchQuery('');
                      }}
                    >
                      <Trans>Show all</Trans>
                    </Button>
                  </VStack>
                ) : null}
                {/* Raw px is the virtualiser's unit. Do NOT pxToRem this or the
                    spacer and the translateY row offsets desync and scrolling loops. */}
                <Box position="relative" height={`${virtualizer.getTotalSize()}px`}>
                  {virtualizer.getVirtualItems().map((virtualItem) => {
                    const item = visibleRows[virtualItem.index]!;
                    return (
                      <Box
                        key={virtualItem.key}
                        ref={virtualizer.measureElement}
                        data-index={virtualItem.index}
                        position="absolute"
                        top={0}
                        insetStart={0}
                        width="full"
                        transform={`translateY(${virtualItem.start}px)`}
                      >
                        {item.kind === 'header' ? (
                          <Box
                            px={4}
                            py={2}
                            bg="bg.subtle"
                            borderBottomWidth="1px"
                            borderColor="border.default"
                            data-testid={testIds.nodeGroupHeader}
                            sx={
                              pulsedGroupId != null && item.group.groupId === pulsedGroupId
                                ? {
                                    animation: 'workbenchJumpPulse 500ms ease-out',
                                    '@keyframes workbenchJumpPulse': {
                                      '0%': { backgroundColor: 'var(--chakra-colors-blue-100)' },
                                      '100%': { backgroundColor: 'transparent' },
                                    },
                                  }
                                : undefined
                            }
                          >
                            <Text
                              fontSize="xs"
                              fontWeight="semibold"
                              color="fg.muted"
                              dir="ltr"
                              noOfLines={2}
                            >
                              {groupLabel(item.group) ?? <Trans>Page content</Trans>}
                            </Text>
                          </Box>
                        ) : (
                          <SeamRow
                            row={item.row}
                            parentQuestion={item.parentQuestion}
                            dir={dir}
                            isEditing={editingKey === item.row.segment.segmentKey}
                            isReadOnly={isGridLocked}
                            onStartEditing={() => setEditingKey(item.row.segment.segmentKey)}
                            onChange={(value) => onChange?.(item.row.segment.segmentKey, value)}
                            onResetToMachine={() => onResetToMachine?.(item.row)}
                          />
                        )}
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Flex>
          </Box>
        </Grid>
      )}
    </Flex>
  );
}

// -- Save control (header + status) ------------------------------------------

function SaveControl({
  surfaces,
  onSave,
  onRetry,
}: {
  surfaces: ReturnType<typeof deriveLifecycleSurfaces>;
  onSave?: () => void;
  onRetry?: () => void;
}) {
  const { header, button } = surfaces;

  return (
    <HStack spacing={3}>
      {/* Fixed min width so state changes don't reflow the toolbar. */}
      <Box minW={pxToRem(180)} display="flex" justifyContent="flex-end" aria-live="polite">
        <HStack spacing="2" color="fg.muted" flexShrink={0} data-testid={testIds.saveStatus}>
          {header.showSpinner ? (
            <Spinner size="xs" color={lifecycleToneColour[header.tone]} flexShrink={0} />
          ) : (
            <Box
              boxSize="2"
              borderRadius="full"
              bg={lifecycleToneColour[header.tone]}
              flexShrink={0}
            />
          )}
          <Text
            fontSize="sm"
            whiteSpace="nowrap"
            color={header.tone === 'warning' ? 'warning.emphasis' : 'fg.muted'}
          >
            {header.label}
          </Text>
        </HStack>
      </Box>
      <Tooltip
        isDisabled={button.isRetry}
        label={t`Your edits are saved as soon as you click Save. Only the lines you changed are re-translated; the rest carry over. This rebuilds the translated file, it does not re-run the whole translation.`}
      >
        <Button
          size="sm"
          variant="primary"
          bg={button.isRetry ? 'orange.500' : undefined}
          _hover={button.isRetry ? { bg: 'orange.600' } : undefined}
          isLoading={button.showSpinner}
          isDisabled={button.disabled}
          onClick={() => (button.isRetry ? onRetry?.() : onSave?.())}
          data-testid={testIds.saveButton}
        >
          {button.label}
        </Button>
      </Tooltip>
    </HStack>
  );
}

// -- Failed strip -------------------------------------------------------------

function FailedStrip({ strip }: { strip: ReturnType<typeof deriveLifecycleSurfaces>['strip'] }) {
  if (!strip.visible) {
    return null;
  }
  return (
    <HStack
      key={strip.announceKey}
      role="alert"
      spacing={3}
      mt={3}
      px={3}
      py={2}
      borderRadius="md"
      bg="bg.warning-subtle"
      data-testid={testIds.failedBanner}
    >
      <Icon as={AlertTriangle} boxSize={iconSize.sm} color="warning.emphasis" flexShrink={0} />
      <Text fontSize="sm" color="fg.default" flex="1">
        {strip.text}
      </Text>
    </HStack>
  );
}

// The locator + segment key written on save come straight from extractSegments
// output, never recomputed on the client.
export function OverrideWorkbenchData({
  resourceId,
  resourceVersionId,
  resourceKind,
  publishedVersion,
  language,
  onSelectLanguage,
  onBack,
  closeButtonRef,
  fillHeight = false,
}: {
  resourceId: string;
  resourceVersionId: number;
  resourceKind: TranslatableResourceKind;
  publishedVersion: number | null;
  language: string;
  onSelectLanguage?: (code: string) => void;
  onBack: () => void;
  closeButtonRef?: RefObject<HTMLButtonElement | null>;
  fillHeight?: boolean;
}) {
  const utils = trpcAdmin.useUtils();

  const [phase, setPhase] = useState<LifecyclePhase>('idle');
  const [appliedEditCount, setAppliedEditCount] = useState<number | null>(null);
  const [attemptId, setAttemptId] = useState(0);

  const isRebuilding = phase === 'applying';

  const translations = useContentTranslations({
    resourceVersionId,
    resourceKind,
    isPanelOpen: true,
  });
  const languageEntry = useMemo(
    () => translations.languages.find((entry) => entry.code === language),
    [translations.languages, language],
  );
  const languageStatus = languageEntry?.status;
  const generatedAtMs = languageEntry?.row?.generatedAt?.getTime() ?? null;

  const segmentsQuery = trpcAdmin.translationOverrides.extractSegments.useQuery(
    { resourceVersionId, language },
    {
      // No refetching mid-rebuild: the stored machine values read back empty and
      // would flip every row to english-shown until the post-rebuild invalidate.
      refetchOnWindowFocus: !isRebuilding,
      refetchOnReconnect: !isRebuilding,
      refetchOnMount: !isRebuilding,
      staleTime: isRebuilding ? Infinity : 0,
    },
  );
  const overridesQuery = trpcAdmin.translationOverrides.list.useQuery({ resourceId, language });

  const savedOverrideCount = overridesQuery.data?.rows.length ?? 0;

  const [drafts, setDrafts] = useState<Record<string, string>>({});

  const rows = useMemo<EditableSegment[]>(() => {
    const segments = segmentsQuery.data?.segments ?? [];
    const overridesByKey = new Map<string, OverrideRow>(
      (overridesQuery.data?.rows ?? []).map((row) => [row.segmentKey, row]),
    );
    return segments.map((segment) => {
      const override = overridesByKey.get(segment.segmentKey);
      const saved = override?.overrideValue ?? segment.currentMachineValue ?? '';
      return {
        segment,
        override,
        draftValue: drafts[segment.segmentKey] ?? saved,
      };
    });
  }, [segmentsQuery.data?.segments, overridesQuery.data?.rows, drafts]);

  const groups = useMemo(() => groupSegments(rows), [rows]);
  const dirtyRows = useMemo(() => rows.filter(isDirty), [rows]);

  // No timestamp: the rows' updatedAt is bumped by every worker rebuild, so it
  // would read as a save time the author never made.
  const editVersions = useMemo<EditVersionEntry[]>(() => {
    const overrideRows = overridesQuery.data?.rows ?? [];
    if (overrideRows.length === 0) {
      return [{ id: 'baseline', kind: 'baseline', isCurrent: true }];
    }
    return [
      {
        id: 'current',
        kind: 'edit',
        isCurrent: true,
        editedSegmentCount: overrideRows.length,
      },
      { id: 'baseline', kind: 'baseline', isCurrent: false },
    ];
  }, [overridesQuery.data?.rows]);

  const saveMany = trpcAdmin.translationOverrides.saveMany.useMutation();
  const retryRebuild = trpcAdmin.contentTranslations.retry.useMutation();
  const clearAll = trpcAdmin.translationOverrides.clearAll.useMutation();

  // Two independent signals resolve "applying": seeing an in-flight status, or the
  // served artifact's generatedAt advancing past the value at Save time. A fast
  // rebuild can finish between two polls so the in-flight status is never observed;
  // the timestamp catches that case. The generatedAt guard also stops the poll that
  // still reports the pre-save available from reading as a completed rebuild.
  const sawInFlightSinceApply = useRef(false);
  const generatedAtAtApply = useRef<number | null>(null);
  const doneTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => window.clearTimeout(doneTimer.current), []);

  // The immediate refetch picks up the freshly-reset PENDING without waiting a poll interval.
  const enterApplying = (editCount: number | null) => {
    setAppliedEditCount(editCount);
    setAttemptId((attempt) => attempt + 1);
    sawInFlightSinceApply.current = false;
    generatedAtAtApply.current = generatedAtMs;
    setPhase('applying');
    void translations.refetch();
  };

  // Drive the applying -> done / failed transitions from the live status, never a
  // timer.
  useEffect(() => {
    if (phase !== 'applying' || languageStatus === undefined) {
      return;
    }
    if (languageStatus === 'queued' || languageStatus === 'generating') {
      sawInFlightSinceApply.current = true;
      return;
    }
    if (languageStatus === 'failed') {
      setPhase('failed');
      return;
    }
    // Done once the rebuild is observable: either we caught an in-flight status,
    // or the served artifact was regenerated (generatedAt moved past Save time).
    const rebuilt =
      generatedAtMs !== null &&
      generatedAtMs !== generatedAtAtApply.current &&
      (generatedAtAtApply.current === null || generatedAtMs > generatedAtAtApply.current);
    if (isTranslatedStatus(languageStatus) && (sawInFlightSinceApply.current || rebuilt)) {
      setPhase('done');
      void utils.translationOverrides.list.invalidate({ resourceId, language });
      void utils.translationOverrides.extractSegments.invalidate({ resourceVersionId, language });
      // A toast too: it outlives a closed modal.
      toast({
        status: 'success',
        title: t`Translation updated`,
        description:
          appliedEditCount == null
            ? t`Your edits are live.`
            : t`Your ${appliedEditCount} edits are live.`,
      });
      window.clearTimeout(doneTimer.current);
      doneTimer.current = setTimeout(() => setPhase('idle'), 2000);
    }
  }, [
    phase,
    languageStatus,
    generatedAtMs,
    utils,
    resourceId,
    resourceVersionId,
    language,
    appliedEditCount,
  ]);

  // Overrides persist the instant this resolves, before any rebuild: the
  // lossless primitive shared by Save and autosave-on-switch. One call writes
  // every changed row and enqueues a single rebuild (not one per row).
  const persistDirtyRows = async (
    rowsToSave: EditableSegment[],
    targetLanguage: string,
  ): Promise<{ enqueued: boolean }> => {
    const { enqueued } = await saveMany.mutateAsync({
      resourceId,
      resourceKind,
      language: targetLanguage,
      edits: rowsToSave.map((row) => ({
        segmentKey: row.segment.segmentKey,
        locator: row.segment.locator,
        // A trimmed-empty value clears the saved override server-side rather
        // than pinning an empty string.
        overrideValue: row.draftValue.trim(),
        sourceTextSnapshot: row.segment.sourceText,
        nodeId: row.segment.nodeId ?? undefined,
      })),
    });
    void utils.translationOverrides.list.invalidate({ resourceId, language: targetLanguage });
    return { enqueued };
  };

  const handleSave = async () => {
    if (dirtyRows.length === 0) {
      return;
    }
    const editCount = dirtyRows.length;
    setPhase('saving');
    try {
      const { enqueued } = await persistDirtyRows(dirtyRows, language);
      if (enqueued) {
        enterApplying(editCount);
        toast({
          status: 'success',
          title: t`Edits saved`,
          description: t`Applying your edits. They go live once the translation finishes.`,
        });
      } else {
        setPhase('idle');
        toast({
          status: 'success',
          title: t`Edits saved`,
          description: t`Your edits will apply the next time you publish this resource.`,
        });
      }
    } catch {
      setPhase('idle');
      toast({
        status: 'error',
        title: t`Couldn't save your edits`,
        description: t`Something went wrong. Try again in a moment.`,
      });
    }
  };

  // Autosaves the leaving language's dirty rows, then moves. Overrides are
  // durable, so save-then-switch is lossless and never needs a blocking prompt.
  const handleSelectLanguage = async (code: string) => {
    if (code === language) {
      return;
    }
    if (dirtyRows.length > 0) {
      const leavingRows = dirtyRows;
      const leavingLanguage = language;
      try {
        await persistDirtyRows(leavingRows, leavingLanguage);
        toast({
          status: 'success',
          title: t`Edits saved`,
          description: t`Your changes were saved before switching languages.`,
        });
      } catch {
        toast({
          status: 'error',
          title: t`Couldn't save your edits before switching`,
          description: t`Something went wrong saving your changes. They're still here; try again.`,
        });
        return;
      }
    }
    onSelectLanguage?.(code);
  };

  // Drafts are keyed by segment key, shared across languages, so they must clear
  // or the incoming language inherits the outgoing one's edits.
  useEffect(() => {
    setDrafts({});
    setPhase('idle');
    setAppliedEditCount(null);
    sawInFlightSinceApply.current = false;
    window.clearTimeout(doneTimer.current);
  }, [language]);

  useEffect(() => {
    if (phase === 'done' && dirtyRows.length > 0) {
      window.clearTimeout(doneTimer.current);
      setPhase('idle');
    }
  }, [phase, dirtyRows.length]);

  const handleRetryRebuild = () => {
    retryRebuild.mutate(
      { resourceVersionId, resourceKind, language },
      {
        onSuccess: (data) => {
          if (!data.enqueued) {
            toast({
              status: 'warning',
              title: t`Couldn't start translating`,
              description: t`Couldn't start translating this language. Try again in a moment.`,
            });
            return;
          }
          enterApplying(appliedEditCount);
        },
        onError: () => {
          toast({
            status: 'error',
            title: t`Couldn't start translating`,
            description: t`Couldn't start translating this language. Try again in a moment.`,
          });
        },
      },
    );
  };

  const handleRestoreBaseline = () => {
    const clearedCount = savedOverrideCount;
    setDrafts({});
    clearAll.mutate(
      { resourceId, resourceKind, language },
      {
        onSuccess: (data) => {
          void utils.translationOverrides.list.invalidate({ resourceId, language });
          if (data.enqueued) {
            enterApplying(clearedCount);
          }
          toast({
            status: 'success',
            title: t`Restoring the auto-translated version`,
            description: t`Your edits have been removed. The translation is rebuilding from the original text.`,
          });
        },
        onError: () => {
          toast({
            status: 'error',
            title: t`Couldn't restore the auto-translated version`,
            description: t`Something went wrong. Try again in a moment.`,
          });
        },
      },
    );
  };

  // A language opened cold into a live failed status must still surface the
  // recovery strip.
  const effectivePhase: LifecyclePhase =
    phase === 'idle' && languageStatus === 'failed' ? 'failed' : phase;

  return (
    <OverrideWorkbenchView
      language={language}
      languages={onSelectLanguage != null ? translations.languages : []}
      resourceKind={resourceKind}
      publishedVersion={publishedVersion}
      editVersions={editVersions}
      groups={groups}
      isLoading={segmentsQuery.isLoading || overridesQuery.isLoading}
      isError={segmentsQuery.isError}
      phase={effectivePhase}
      dirtyCount={dirtyRows.length}
      appliedEditCount={appliedEditCount}
      attemptId={attemptId}
      onBack={onBack}
      closeButtonRef={closeButtonRef}
      onRetryLoad={() => {
        void segmentsQuery.refetch();
        void overridesQuery.refetch();
      }}
      onChange={(segmentKey, value) =>
        setDrafts((previous) => ({ ...previous, [segmentKey]: value }))
      }
      onResetToMachine={(row) =>
        // Empty the field: on save an empty value deletes the override, a true
        // revert to the auto-translated value rather than pinning a new override
        // equal to the machine text. The row previews the machine value as its
        // placeholder while empty.
        setDrafts((previous) => ({ ...previous, [row.segment.segmentKey]: '' }))
      }
      onSave={() => void handleSave()}
      onSelectLanguage={
        onSelectLanguage == null ? undefined : (code) => void handleSelectLanguage(code)
      }
      onRetryRebuild={handleRetryRebuild}
      isRetryingRebuild={retryRebuild.isPending}
      onRestoreBaseline={handleRestoreBaseline}
      isRestoringBaseline={clearAll.isPending}
      fillHeight={fillHeight}
    />
  );
}
